import AbstractGenerator from '@scene/misc/generator';

import baseOptions from './defaultOptions';

import Ground, { LabelsPadding } from '../../objects/ground';
import Arrow from '../../objects/arrow';
import Reinforcement from './objects/reinforcement';
import { HTMLLabel, HTMLEditableLabel } from '../../objects/html-label';
import Arrow3D from '@scene/objects/arrow3D';
import { defaultUnits } from '@config';
import Scene from "@scene";
import { unitTransform } from '@services/helper.service';
import * as THREE from 'three';
import { APP_TEXTS } from '@texts';

export default class RebarReinforcement extends AbstractGenerator {

  constructor(options = {}) {
    super(options, baseOptions);

    this.actionType = this.options.reinforcement.actionType;
  }

  initCommon() {
    this.ground = new Ground(this.options.ground, null, { isRebar: true });
    this.ground.onOptionUpdate(this.dispatchOptionUpdate.bind(this));

    this.reinforcement = new Reinforcement({
      ...this.options.reinforcement,
      screw: this.options.screw,
      width: this.options.reinforcement.width.active ? this.options.reinforcement.width.value : this.options.ground.width,
      height: this.options.reinforcement.height.active ? this.options.reinforcement.height.value : this.options.ground.height,
      depth: this.options.reinforcement.depth.value 
            //this.options.reinforcement.depth.active ? this.options.reinforcement.depth.value : this.options.ground.depth
    });
    this.reinforcement.onOptionUpdate(this.dispatchOptionUpdate.bind(this));
    this.reinforcement.position.z = this.ground.size.depth;
  }

  init3DObject() {
    this.initCommon();

    this.layer3D.addObject(this.ground);
    this.layer3D.addObject(this.reinforcement);

    if (this.actionType.active && this.options.object.loads.display) {
      this.createActionTypeArrow();
    }

    if (this.options.object.dimensions.display) {
      this.addLabels();
    }
  }

  init2DObject() {
    this.initCommon();

    this.layer2D.addObject(this.ground);
    this.layer2D.addObject(this.reinforcement);

    this.addLabels();
  }

  addLabels() {
    const layer = this.is3DMode ? this.layer3D : this.layer2D;
    let options = null;
    if (this.is3DMode) {
      options = {
        is3DMode: true,
        showLabels: this.options.object.dimensions.showLabels,
        displayHorizontally: this.options.object.dimensions.displayHorizontally,
        isSafetyBarrier: false
      };
    }

    switch (this.options.orientation) {
      case AbstractGenerator.orientation.X:
      default: {
        // Create Wa label
        const waLabel = this.ground.createWidthLabel('Wa', options);
        if (this.is3DMode) {
          waLabel.domElement.position.set(
            this.ground.position.z + waLabel.domElement.position.y,
            this.ground.position.y - waLabel.domElement.position.z + 25,
            this.ground.position.x + waLabel.domElement.position.x
          );
          if (!options.displayHorizontally) {
            waLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            waLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            waLabel.domElement.setDirection(
              waLabel.domElement.position.clone().setZ(
                waLabel.domElement.position.z + Math.max(25, this.ground.size.width / 2)),
              waLabel.domElement.position.clone().setZ(
                waLabel.domElement.position.z - Math.max(25, this.ground.size.width / 2)),
              false
            );
          }
        } 
        layer.htmlLayer.add(waLabel.domElement);
    
        // Create Ha label
        let haLabel = null;
        if (this.is3DMode) {
          haLabel = this.ground.createHeightLabel('Ha', options, - LabelsPadding * 2);
          haLabel.domElement.position.set(
            this.ground.position.z + haLabel.domElement.position.z,
            this.ground.position.y + haLabel.domElement.position.x,
            this.ground.position.x - haLabel.domElement.position.y + 25
          );
          if (!options.displayHorizontally) {
            haLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            haLabel.domElement.setDirection(
              haLabel.domElement.position.clone().setY(
                haLabel.domElement.position.y + Math.max(25, this.ground.size.height / 2)),
              haLabel.domElement.position.clone().setY(
                haLabel.domElement.position.y - Math.max(25, this.ground.size.height / 2)),
              false
            );
          }
        } else {
          haLabel = this.ground.createHeightLabel('Ha', options, - LabelsPadding * 6);
          haLabel.domElement.position.x = this.ground.size.width / -2 - LabelsPadding * 6;
          haLabel.domElement.position.z = 0;
        }
        layer.htmlLayer.add(haLabel.domElement);
    
        // Create La label
        let laLabel = null; 
        if (this.is3DMode) {
          laLabel = this.ground.create3DDepthLabelRebar('La', options);
          layer.addMesh(laLabel.object);
          laLabel.domElement.position.set(
            this.ground.position.z + laLabel.domElement.position.z,
            this.ground.position.y - laLabel.domElement.position.x,
            this.ground.position.x - laLabel.domElement.position.y + 25
          );
          if (!options.displayHorizontally) {
            laLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            laLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            laLabel.domElement.setDirection(
              laLabel.domElement.position.clone().setX(
                laLabel.domElement.position.x + Math.max(25, this.ground.size.depth / 2)),
              laLabel.domElement.position.clone().setX(
                laLabel.domElement.position.x - Math.max(25, this.ground.size.depth / 2)),
              false
            );
          }
        } else {
          laLabel = this.ground.createDepthLabel('La', options);
        }
        layer.htmlLayer.add(laLabel.domElement);
    
        // Create Wb label
        if (this.options.reinforcement.width.active) {
          const wbLabel = this.reinforcement.createWidthLabel('Wb', options);
          if (this.is3DMode) {
            wbLabel.domElement.position.set(
              this.reinforcement.position.z + wbLabel.domElement.position.y,
              this.reinforcement.position.y - wbLabel.domElement.position.z + 25,
              this.reinforcement.position.x + wbLabel.domElement.position.x
            );
            if (!options.displayHorizontally) {
              wbLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
              wbLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
              wbLabel.domElement.setDirection(
                wbLabel.domElement.position.clone().setZ(
                  wbLabel.domElement.position.z + Math.max(25, this.reinforcement.size.width / 2)),
                wbLabel.domElement.position.clone().setZ(
                  wbLabel.domElement.position.z - Math.max(25, this.reinforcement.size.width / 2)),
                false
              );
            }
          } 
          layer.htmlLayer.add(wbLabel.domElement);
        }
    
        // Create Hb label
        if (this.options.reinforcement.height.active) {
          let hbLabel = this.reinforcement.createHeightLabel('Hb', options);
          if (this.is3DMode) {
            hbLabel.domElement.position.set(
              this.reinforcement.position.z + hbLabel.domElement.position.z,
              this.reinforcement.position.y + hbLabel.domElement.position.x,
              this.reinforcement.position.x - hbLabel.domElement.position.y - 25
            );
            if (!options.displayHorizontally) {
              hbLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
              hbLabel.domElement.setDirection(
                hbLabel.domElement.position.clone().setY(
                  hbLabel.domElement.position.y + Math.max(25, this.options.reinforcement.height.value / 2)),
                hbLabel.domElement.position.clone().setY(
                  hbLabel.domElement.position.y - Math.max(25, this.options.reinforcement.height.value / 2)),
                false
              );
            }
          } else {
            hbLabel.domElement.position.x = this.reinforcement.size.width / 2 + LabelsPadding * 2;
            hbLabel.domElement.position.z = 0;
          }
          layer.htmlLayer.add(hbLabel.domElement);
        }
        
        // Create D label
        if (this.options.screw.count.n > 1) {
          let dLabel = this.reinforcement.createDLabel('D', options);
          if (this.is3DMode) {
            dLabel.domElement.position.set(
              this.reinforcement.position.z + dLabel.domElement.position.z,
              this.reinforcement.position.y - dLabel.domElement.position.x,
              this.reinforcement.position.x - dLabel.domElement.position.y + 25
            );
            if (!options.displayHorizontally) {
              dLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
              dLabel.domElement.setDirection(
                dLabel.domElement.position.clone().setY(
                  dLabel.domElement.position.y + Math.max(25, this.options.screw.padding.d / 2)),
                dLabel.domElement.position.clone().setY(
                  dLabel.domElement.position.y - Math.max(25, this.options.screw.padding.d / 2)),
                false
              );
            }
          } else {
            dLabel.domElement.position.x = dLabel.position.bottom.to.x;
            dLabel.domElement.position.z = this.reinforcement.size.height / 2 - this.options.screw.shift.c - 
              this.options.screw.padding.d / 2 - this.options.screw.radius;
          }
          layer.htmlLayer.add(dLabel.domElement);
        }
    
        // Create S label
        const sLabel = this.reinforcement.createSLabel('S', options);
        if (this.is3DMode) {
          sLabel.domElement.position.set(
            this.reinforcement.position.z + sLabel.domElement.position.y,
            this.reinforcement.position.y - sLabel.domElement.position.z - 25,
            this.reinforcement.position.x + sLabel.domElement.position.x
          );
          if (!options.displayHorizontally) {
            sLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            sLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            sLabel.domElement.setDirection(
              sLabel.domElement.position.clone().setZ(
                sLabel.domElement.position.z + Math.max(25, this.options.screw.padding.s / 2)),
              sLabel.domElement.position.clone().setZ(
                sLabel.domElement.position.z - Math.max(25, this.options.screw.padding.s / 2)),
              false
            );
          }
        } else {
          sLabel.domElement.position.x = -this.reinforcement.size.width / 2 + 
            this.options.screw.shift.c1 + this.options.screw.radius + this.options.screw.padding.s / 2;
        }
        layer.htmlLayer.add(sLabel.domElement);
    
        // Create C label
        const cLabel = this.reinforcement.createCLabel('C', options);
        if (this.is3DMode) {
          cLabel.domElement.position.set(
            this.reinforcement.position.z + cLabel.domElement.position.z,
            this.reinforcement.position.y - cLabel.domElement.position.x,
            this.reinforcement.position.x - cLabel.domElement.position.y + 25
          );
          if (!options.displayHorizontally) {
            cLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            cLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            cLabel.domElement.setDirection(
              cLabel.domElement.position.clone().setY(
                cLabel.domElement.position.y + Math.max(25, this.options.screw.shift.c / 2)),
              cLabel.domElement.position.clone().setY(
                cLabel.domElement.position.y - Math.max(25, this.options.screw.shift.c / 2)),
              false
            );
          }
        } else {
          cLabel.domElement.position.x = cLabel.position.bottom.to.x;
          cLabel.domElement.position.z = this.reinforcement.size.height / 2 - this.options.screw.shift.c / 2;
        }
        layer.htmlLayer.add(cLabel.domElement);
    
        // Create C1 label
        const c1Label = this.reinforcement.createC1Label('C1', options);
        if (this.is3DMode) {
          c1Label.domElement.position.set(
            this.reinforcement.position.z + c1Label.domElement.position.y,
            this.reinforcement.position.y - c1Label.domElement.position.z - 25,
            this.reinforcement.position.x + c1Label.domElement.position.x
          );
          if (!options.displayHorizontally) {
            c1Label.domElement.align = HTMLEditableLabel.types.align.CENTER;
            c1Label.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            c1Label.domElement.setDirection(
              c1Label.domElement.position.clone().setZ(
                c1Label.domElement.position.z + Math.max(25, this.options.screw.shift.c1 / 2)),
              c1Label.domElement.position.clone().setZ(
                c1Label.domElement.position.z - Math.max(25, this.options.screw.shift.c1 / 2)),
              false
            );
          }
        } else {
          c1Label.domElement.position.x = -this.reinforcement.size.width / 2 + this.options.screw.shift.c1 / 2;
        }
        layer.htmlLayer.add(c1Label.domElement);
        break;
      }
      case AbstractGenerator.orientation.Y: {
        // Create Wa label
        let waLabel = this.ground.createWidthLabel('Wa', options);
        if (this.is3DMode) {
          waLabel.object.rotation.z += Math.PI;
          waLabel.domElement.ignoreGlobalRotation = true;
          waLabel.domElement.position.set(
            this.ground.position.z - waLabel.domElement.position.z + 25,
            this.ground.position.y + waLabel.domElement.position.y,
            this.ground.position.x + waLabel.domElement.position.x
          );
          if (!options.displayHorizontally) {
            waLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            waLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            waLabel.domElement.setDirection(
              waLabel.domElement.position.clone().setZ(
                waLabel.domElement.position.z + Math.max(25, this.ground.size.width / 2)),
              waLabel.domElement.position.clone().setZ(
                waLabel.domElement.position.z - Math.max(25, this.ground.size.width / 2)),
              false
            );
          }
        } else {
          waLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
          waLabel.domElement.align = HTMLEditableLabel.types.align.RIGHT;
          waLabel.domElement.position.x = -waLabel.position.top.to.y;
          waLabel.domElement.position.z = 0;
        }
        layer.htmlLayer.add(waLabel.domElement);
    
        // Create Ha label
        let haLabel = null;
        if (this.is3DMode) {
          haLabel = this.ground.createHeightLabel('Ha', options, - LabelsPadding * 2);
          haLabel.domElement.ignoreGlobalRotation = true;
          haLabel.domElement.position.set(
            this.ground.position.z + haLabel.domElement.position.x,
            this.ground.position.y + haLabel.domElement.position.z,
            this.ground.position.x - haLabel.domElement.position.y + 25
          );
          if (!options.displayHorizontally) {
            haLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            haLabel.domElement.setDirection(
              haLabel.domElement.position.clone().setX(
                haLabel.domElement.position.x + Math.max(25, this.ground.size.height / 2)),
              haLabel.domElement.position.clone().setX(
                haLabel.domElement.position.x - Math.max(25, this.ground.size.height / 2)),
              false
            );
          }
        } else {
          haLabel = this.ground.createHeightLabel('Ha', options, - LabelsPadding * 6);
          haLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
          haLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
          haLabel.domElement.position.x = 0;
          haLabel.domElement.position.z = -haLabel.position.top.to.x;
        }
        layer.htmlLayer.add(haLabel.domElement);
    
        // Create La label
        let laLabel = null; 
        if (this.is3DMode) {
          laLabel = this.ground.create3DDepthLabelRebar('La', options);
          layer.addMesh(laLabel.object);
          laLabel.domElement.ignoreGlobalRotation = true;
          laLabel.domElement.position.set(
            this.ground.position.z + laLabel.domElement.position.x,
            this.ground.position.y + laLabel.domElement.position.z,
            this.ground.position.x - laLabel.domElement.position.y + 25
          );
          if (!options.displayHorizontally) {
            laLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            laLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            laLabel.domElement.setDirection(
              laLabel.domElement.position.clone().setY(
                laLabel.domElement.position.y + Math.max(25, this.ground.size.depth / 2)),
              laLabel.domElement.position.clone().setY(
                laLabel.domElement.position.y - Math.max(25, this.ground.size.depth / 2)),
              false
            );
          }
        } else {
          laLabel = this.ground.createDepthLabel('La', options);
          laLabel.object.rotation.z = Math.PI / -2;
          laLabel.object.position.x = this.ground.size.width / 2 - LabelsPadding;
          laLabel.object.position.z = this.ground.size.height / -2 - LabelsPadding; 
          laLabel.domElement.position.x = this.ground.size.height / 2 - LabelsPadding + laLabel.path[1].x;
          laLabel.domElement.position.z = this.ground.size.width / -2 + LabelsPadding - laLabel.path[1].y;
        }
        layer.htmlLayer.add(laLabel.domElement);
    
        // Create Wb label - there's no configuration that allows Wb parameter on this orientation
        // if (this.options.reinforcement.width.active) {
        //   const {domElement: reinforcementWidthHtmlElement} = this.reinforcement.createWidthLabel('Wb', options);
        //   layer.htmlLayer.add(reinforcementWidthHtmlElement);
        // }
    
        // Create Hb label
        if (this.options.reinforcement.height.active) { 
          let hbLabel = this.reinforcement.createHeightLabel('Hb', options);
          if (this.is3DMode) {
            hbLabel.domElement.ignoreGlobalRotation = true;
            hbLabel.domElement.position.set(
              this.reinforcement.position.x + hbLabel.domElement.position.x,
              this.reinforcement.position.z + hbLabel.domElement.position.z,
              this.reinforcement.position.y - hbLabel.domElement.position.y - 25
            );
            if (!options.displayHorizontally) {
              hbLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
              hbLabel.domElement.setDirection(
                hbLabel.domElement.position.clone().setX(
                  hbLabel.domElement.position.x + Math.max(25, this.options.reinforcement.height.value / 2)),
                hbLabel.domElement.position.clone().setX(
                  hbLabel.domElement.position.x - Math.max(25, this.options.reinforcement.height.value / 2)),
                false
              );
            }
          } else {
            hbLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            hbLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.BOTTOM;
            hbLabel.domElement.position.x = 0;
            hbLabel.domElement.position.z = - this.reinforcement.size.width / 2 - LabelsPadding * 2;
          }
          layer.htmlLayer.add(hbLabel.domElement);
        }
        
        // Create D label
        if (this.options.screw.count.n > 1) {
          let dLabel = this.reinforcement.createDLabel('D', options);
          if (this.is3DMode) {
            dLabel.domElement.ignoreGlobalRotation = true;
            dLabel.domElement.position.set(
              this.reinforcement.position.x + dLabel.domElement.position.x,
              this.reinforcement.position.z + dLabel.domElement.position.z,
              this.reinforcement.position.y - dLabel.domElement.position.y + 25
            );
            if (!options.displayHorizontally) {
              dLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
              dLabel.domElement.setDirection(
                dLabel.domElement.position.clone().setX(
                  dLabel.domElement.position.x + Math.max(25, this.options.screw.padding.d / 2)),
                dLabel.domElement.position.clone().setX(
                  dLabel.domElement.position.x - Math.max(25, this.options.screw.padding.d / 2)),
                false
              );
            }
          } else {
            dLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
            dLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            dLabel.domElement.position.x = -dLabel.position.top.to.y - this.options.screw.padding.d / 2;
            dLabel.domElement.position.z = -dLabel.position.top.to.x;
            // dLabel.domElement.position.x = dLabel.position.bottom.to.x;
            // dLabel.domElement.position.z = this.reinforcement.size.height / 2 - this.options.screw.shift.c - 
            //   this.options.screw.padding.d / 2 - this.options.screw.radius;
          }
          layer.htmlLayer.add(dLabel.domElement);
        }
    
        // Create S label
        const sLabel = this.reinforcement.createSLabel('S', options);
        if (this.is3DMode) {
          sLabel.domElement.ignoreGlobalRotation = true;
          sLabel.domElement.position.set(
            this.reinforcement.position.x + sLabel.domElement.position.z + 25,
            this.reinforcement.position.z + sLabel.domElement.position.y,
            this.reinforcement.position.y + sLabel.domElement.position.x
          );
          if (!options.displayHorizontally) {
            sLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            sLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            sLabel.domElement.setDirection(
              sLabel.domElement.position.clone().setZ(
                sLabel.domElement.position.z + Math.max(25, this.options.screw.padding.s / 2)),
              sLabel.domElement.position.clone().setZ(
                sLabel.domElement.position.z - Math.max(25, this.options.screw.padding.s / 2)),
              false
            );
          }
        } else {
          sLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
          sLabel.domElement.align = HTMLEditableLabel.types.align.Left;
          sLabel.domElement.position.x = -sLabel.position.top.to.y;
          sLabel.domElement.position.z = -sLabel.position.top.to.x - this.options.screw.padding.s / 2;
        }
        layer.htmlLayer.add(sLabel.domElement);
    
        // Create C label
        const cLabel = this.reinforcement.createCLabel('C', options);
        if (this.is3DMode) {
          cLabel.domElement.ignoreGlobalRotation = true;
          cLabel.domElement.position.set(
            this.reinforcement.position.x + cLabel.domElement.position.x,
            this.reinforcement.position.z + cLabel.domElement.position.z,
            this.reinforcement.position.y - cLabel.domElement.position.y + 25
          );
          if (!options.displayHorizontally) {
            cLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
            cLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            cLabel.domElement.setDirection(
              cLabel.domElement.position.clone().setX(
                cLabel.domElement.position.x + Math.max(25, this.options.screw.shift.c / 2)),
              cLabel.domElement.position.clone().setX(
                cLabel.domElement.position.x - Math.max(25, this.options.screw.shift.c / 2)),
              false
            );
          }
        } else {
          cLabel.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.TOP;
          cLabel.domElement.align = HTMLEditableLabel.types.align.CENTER;
          cLabel.domElement.position.x = -cLabel.position.top.to.y - this.options.screw.shift.c / 2;
          cLabel.domElement.position.z = -cLabel.position.top.to.x;
        }
        layer.htmlLayer.add(cLabel.domElement);
    
        // Create C1 label
        const c1Label = this.reinforcement.createC1Label('C1', options);
        if (this.is3DMode) {
          c1Label.domElement.ignoreGlobalRotation = true;
          c1Label.domElement.position.set(
            this.reinforcement.position.x + c1Label.domElement.position.z + 25,
            this.reinforcement.position.z + c1Label.domElement.position.y,
            this.reinforcement.position.y + c1Label.domElement.position.x
          );
          if (!options.displayHorizontally) {
            c1Label.domElement.align = HTMLEditableLabel.types.align.CENTER;
            c1Label.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            c1Label.domElement.setDirection(
              c1Label.domElement.position.clone().setZ(
                c1Label.domElement.position.z + Math.max(25, this.options.screw.shift.c1 / 2)),
              c1Label.domElement.position.clone().setZ(
                c1Label.domElement.position.z - Math.max(25, this.options.screw.shift.c1 / 2)),
              false
            );
          }
        } else {
          c1Label.domElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
          c1Label.domElement.align = HTMLEditableLabel.types.align.Left;
          c1Label.domElement.position.x = -c1Label.position.top.to.y;
          c1Label.domElement.position.z = -c1Label.position.top.to.x - this.options.screw.shift.c1 / 2;
        }
        layer.htmlLayer.add(c1Label.domElement);
        break;
      }
    }

  }

  setOrientation() {
    super.setOrientation();

    if (this.is3DMode) {
      if (this.options.orientation === AbstractGenerator.orientation.Y) {
        this.layer3D.mesh.rotation.x = -Math.PI / 2;
        this.layer3D.mesh.rotation.z = Math.PI / 2;
      }

      this.setHTMLOrientation();
    } else if (this.is2DMode) {
      switch (this.options.orientation) {
        case AbstractGenerator.orientation.Y: {
          this.layer2D.mesh.rotation.x = Math.PI / -2;
          this.layer2D.mesh.rotation.y = 0;
          this.layer2D.mesh.rotation.z = Math.PI / 2;

          break;
        }
        case AbstractGenerator.orientation.X:
        default: {
          this.layer2D.mesh.rotation.x = Math.PI / -2;
          this.layer2D.mesh.rotation.y = 0;
          this.layer2D.mesh.rotation.z = 0;
          break;
        }
      }
    }
  }

  setHTMLOrientation() {
    super.setHTMLOrientation();

    if (this.is3DMode) {
      switch (this.options.orientation) {
        case AbstractGenerator.orientation.X: {
          break;
        }
        case AbstractGenerator.orientation.Y: {
          // move html elements
          for (let i = 0; i < this.layer3D.htmlLayer.htmlLayerElements.length; i++){
            let elem = this.layer3D.htmlLayer.htmlLayerElements[i];
            if (!elem.ignoreGlobalRotation)
              elem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI / 4);
          }
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  createActionTypeArrow() {
    const arrow = new Arrow3D({
      position: this.reinforcement.position,
      size: this.options.reinforcement.depth.value * 2.5,
      color: 0xff0000,
      isSingle: true,
      direction: Arrow.types.direction.NegativeZ,
      thickness: 6
    });

    const LINE = 0;
    arrow.mesh.children[0].children[LINE].material.depthTest = false;

    const CONUS = 1;
    arrow.mesh.children[0].children[CONUS].material.depthTest = false;

    this.layer3D.addObject(arrow);

    let translatedType = this.actionType.type;
    if (translatedType === 'on the section')
      translatedType = APP_TEXTS.REBAR_LOAD_TYPE_ON_SECTION.Content;
    else if (translatedType === 'per rebar')
      translatedType = APP_TEXTS.REBAR_LOAD_TYPE_PER_REBAR.Content;
      
    // const htmlElement = new HTMLLabel({
    //   text: `Nz: ${unitTransform(+this.actionType.value).from(defaultUnits.LOAD).to(Scene.units.load)} ` +
    //   `${Scene.units.load || ''} ${translatedType || ''}`,
    //   align: HTMLLabel.types.align.CENTER,
    //   verticalAlign: HTMLLabel.types.verticalAlign.CENTER
    // });

    let htmlElement = new HTMLEditableLabel({
      text: `Nz: ${unitTransform(+this.actionType.value).from(defaultUnits.LOAD).to(Scene.units.load)} ` +
        `${Scene.units.load || ''} ${translatedType || ''}`,
      value: this.actionType.value,
      // color: RED_HTML,
      verticalAlign: HTMLLabel.types.verticalAlign.CENTER,
      align: HTMLLabel.types.align.CENTER,
      // bold: true
    });
    htmlElement.onInputChange((val, didChange) => {
      this.dispatchOptionUpdate({
        reinforcement: {
          actionType: {
            value: unitTransform(+val, false).from(Scene.units.load).to(defaultUnits.LOAD) 
          }
        }
      },
      false,
      didChange);
    });

    const position = arrow.directionPosition.clone();

    // Add margin
    position.z += 35;

    htmlElement.position.set(position.z, position.x, position.y);

    this.layer3D.htmlLayer.add(htmlElement);

    return { htmlElement, arrow };
  }

}




// WEBPACK FOOTER //
// ./src/object-view/generators/rebar-reinforcement/rebar-reinforcement.object.js